import React, { useContext, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { BreadcrumbItemProps } from '../../../shared/components/Breadcrumbs/BreadcrumbItem/BreadcrumbItem';
import CustomEmptyPage from '../../../shared/components/CustomEmptyPage/CustomEmptyPage';
import PageCurvedContainer from '../../../shared/components/PageCurvedContainer/PageCurvedContainer';
import StyledButtonNE from '../../../shared/components/StyledButtonNE/StyledButtonNE';
import { POINTS_FILE_NAME } from '../../../shared/constants/rewards';
import { CLICK_ACTION, SECTION_REWARDS_SEND_POINTS_FILE, SEND_REQUEST } from '../../../shared/constants/tagManager';
import { CompanyContext } from '../../../shared/context/company/companyContext';
import { RewardsContext } from '../../../shared/context/rewards/rewardsContext';
import { IRewardsFileValidation, ISendPointsFileData } from '../../../shared/interfaces/rewards';
import Rewards from '../../../shared/services/api/rewards/rewards';
import tagManagerService from '../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_POINTS_BY_FILE } from '../../../shared/translations/rewards';
import { TRANSLATIONS_SETTINGS } from '../../../shared/translations/settings';
import { RewardsBalancePaymentMethods } from '../../../shared/types/rewards';
import { toLocaleNumberFormat } from '../../../shared/utils/utils';
import RewardsPointsEmailConfig from '../RewardsPointsEmailConfig/RewardsPointsEmailConfig';
import RewardsPageHeader from '../sections/RewardsPageHeader/RewardsPageHeader';
import RewardsPointsTabBar from '../sections/RewardsPointsTabBar/RewardsPointsTabBar';
import PointsByFileError from './sections/Error/PointsByFileError';
import PartialSuccess from './sections/PartialSuccess/PartialSuccess';
import ShippingDetailCard from './sections/ShippingDetailCard/ShippingDetailCard';
import PointsByFileSuccess from './sections/Success/PointsByFileSuccess';
import UploadFileCard from './sections/UploadFileCard/UploadFileCard';

const RewardsPointsByFile = () => {
  const [validatedData, setValidatedData] = useState<IRewardsFileValidation>();
  const [base64, setBase64] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [successfulPointsSent, setSuccessfulPointsSent] = useState<boolean | null>(null);
  const [orderLabel, setOrderLabel] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [category, setCategory] = useState<string>('default');
  const {
    balance,
    currency,
    loading: rewardsLoading,
    error: rewardsError,
    countryCode,
    internalProducts,
    updateBalance
  } = useContext(RewardsContext);
  const { company } = useContext(CompanyContext);
  const { t } = useTranslation();

  const pointsBreadcrumb: BreadcrumbItemProps[] = [
    { label: t(TRANSLATION_POINTS_BY_FILE.header.breadcrumb.rewards) },
    { label: t(TRANSLATION_POINTS_BY_FILE.header.breadcrumb.sendPoints) }
  ];

  const getValidatedData = (pointsInfo: IRewardsFileValidation | undefined) => {
    setValidatedData(pointsInfo);
  };

  const handleOnChangeMessage = (message: string) => {
    setMessage(message);
  };

  const handleOnChangeCategory = (category: string) => {
    setCategory(category);
  };

  const getFileData = (base64File: string | undefined) => {
    setBase64(base64File);
  };

  const onSendPoints = async () => {
    if (base64 && validatedData) {
      sendPoints(base64, validatedData);
      tagManagerService.pushEvent(CLICK_ACTION, SEND_REQUEST, {
        ...SECTION_REWARDS_SEND_POINTS_FILE,
        category: category,
        has_personalized_message: !!message.length
      });
    }
  };

  const sendPartialSuccessPoints = async (base64File: string, pointsInfo: IRewardsFileValidation) => {
    await sendPoints(base64File, pointsInfo);
    setValidatedData(pointsInfo);
    setBase64(base64File);
  };

  const cleanFields = () => {
    setMessage('');
    setCategory('');
  };

  const sendPoints = async (base64File: string, pointsInfo: IRewardsFileValidation) => {
    const pointsProduct = internalProducts.find((product) => product.type === 'points');
    if (!pointsProduct) {
      return;
    }

    try {
      setLoading(true);

      const errors = pointsInfo.errors
        ? { fileErrorBase64: pointsInfo.errors, errorsCount: pointsInfo.errorsCount }
        : undefined;

      const sendPointsData: ISendPointsFileData = {
        countryCode: countryCode,
        message: message,
        category: category,
        orderAmount: pointsInfo.totalAmount,
        paymentDetail: {
          paymentMethod: RewardsBalancePaymentMethods[RewardsBalancePaymentMethods.balance],
          currency: currency
        },
        productId: pointsProduct.id,
        productName: pointsProduct.name,
        orders: pointsInfo.orders,
        fileBase64: base64File,
        payrollFileName: POINTS_FILE_NAME,
        errors: errors
      };

      const response = await Rewards.sendPoints(company.id, sendPointsData);

      setSuccessfulPointsSent(true);
      setOrderLabel(response.orderLabel);
      cleanFields();
      updateBalance(balance - sendPointsData.orderAmount);
    } catch {
      setSuccessfulPointsSent(false);
    } finally {
      setLoading(false);
    }
  };

  const resetStates = () => {
    setValidatedData(undefined);
    setBase64(undefined);
    setLoading(false);
    setSuccessfulPointsSent(null);
  };

  const hasBalance = () => {
    if (!validatedData?.hasBalance) {
      return false;
    }
    return balance - validatedData.totalAmount >= 0;
  };

  const getMissingBalance = () => {
    return toLocaleNumberFormat(Number(validatedData?.totalAmount) - balance);
  };

  useEffect(() => {
    resetStates();
  }, [company]);

  const dataHasErrors = successfulPointsSent && validatedData && !validatedData.valid;

  if (dataHasErrors) {
    return (
      <PartialSuccess
        orderNumber={orderLabel}
        previousPointsInfo={validatedData}
        sendPoints={sendPartialSuccessPoints}
      />
    );
  }

  if (successfulPointsSent) {
    return <PointsByFileSuccess breadcrumbs={pointsBreadcrumb} orderNumber={orderLabel} resetPage={resetStates} />;
  }

  if (successfulPointsSent === false) {
    return (
      <PointsByFileError
        breadcrumbs={pointsBreadcrumb}
        retry={onSendPoints}
        resetPage={resetStates}
        loading={loading}
      />
    );
  }

  return (
    <div className="page-container curved" data-experience="new">
      <RewardsPageHeader
        breadcrumbs={pointsBreadcrumb}
        rewardsLoading={rewardsLoading}
        rewardsError={rewardsError}
        currency={currency}
        balance={balance}
        title={t(TRANSLATION_POINTS_BY_FILE.header.title)}
        description={t(TRANSLATION_POINTS_BY_FILE.header.description)}
        button={true}
      />
      <PageCurvedContainer>
        {rewardsError ? (
          <CustomEmptyPage
            title={t(TRANSLATIONS_SETTINGS.error.title)}
            subtitle={t(TRANSLATIONS_SETTINGS.error.subtitle)}
            newExperience={true}
          />
        ) : (
          <>
            <RewardsPointsTabBar />

            <UploadFileCard setValidatedData={getValidatedData} setFileInfo={getFileData} />

            {validatedData && (
              <ShippingDetailCard
                currentPointsInfo={validatedData}
                hasBalance={hasBalance()}
                missingBalance={getMissingBalance()}
              />
            )}

            <div className="emails-config">
              <RewardsPointsEmailConfig
                onChangeMessage={handleOnChangeMessage}
                onChangeCategory={handleOnChangeCategory}
                message={message}
                category={category}
              />
            </div>

            <div className="self-end">
              <StyledButtonNE
                buttonType="primary"
                buttonSize="lg"
                action={onSendPoints}
                loading={loading}
                disabled={!validatedData || !base64 || !hasBalance()}
              >
                {t(TRANSLATION_POINTS_BY_FILE.sendPoints)}
              </StyledButtonNE>
            </div>
          </>
        )}
      </PageCurvedContainer>
    </div>
  );
};

export default RewardsPointsByFile;
